import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Button, useBreakpointValue, useMediaQuery} from '@chakra-ui/react'
import {Link} from 'react-router-dom'

// utils/helpers
import {remCalc} from '../../../theme/_utils'
import getSideCardStyles from '../../../theme/components/project/side-card'

//components
import {TextTitle} from '../text-title'
import {TextTitleDescription} from '../text-title-description'
import ResponsiveImage from '../../responsive-image'
import SocialContent from './partials/social-content'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {safeBuildUrl, getGlobalLink} from '../../../utils/contentstack-utils'
import useDamImage from '../../../custom-hooks/use-dam-image'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const onServer = typeof window === 'undefined'

export const SideCard = ({
    textGroup,
    textGroupPdp,
    mobileFullSizePdp = false,
    imageUrl,
    imageGroup, // => useDamImage(imageGroup[`image_${type}`])
    imagePosition = 'right',
    button,
    type, // standard / pdp / full
    appDownloadContent,
    imageAlt, // when dam is integrated this can be removed
    backgroundColor = 'white', // white / black / grey
    hasPaddings,
    videos,
}) => {
    const videoSrc = useBreakpointValue({
        base: videos?.mobile,
        lg: videos?.desktop,
    })
    const [isExpanded, setIsExpanded] = useState(false)
    const descriptionRef = useRef(null)
    const {buildUrl} = useMultiSite()

    const categoryData = useGetCategoriesData()
    const {url, label, isAbsolute} = getGlobalLink(button, categoryData)

    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    const isReversed = imagePosition === 'right'
    // const isStandard = type === 'standard' // default behavior
    const isFull = type === 'full'
    const isPdpPage = type === 'pdp'
    const hasAppDownloads = appDownloadContent?.app_buttons?.length > 0

    const damImage = useDamImage(imageGroup?.[`image_${type}`])

    // determine layout based on screen size
    const layout = useBreakpointValue({
        base: 'vertical', // default
        sm: 'vertical',
        md: isPdpPage ? 'horizontal' : 'vertical',
        lg: 'horizontal',
    })

    // work around for media query between base and md
    const [isSmallTablet] = useMediaQuery(
        `(min-width: ${remCalc(475)}) and (max-width: ${remCalc(767)})`,
    )

    const hasDetails = isPdpPage && textGroupPdp?.bodyCopy

    //TODO: retrieve styles using chakra-ui hooks useMultiStyle/useMultiStyleConfig
    const styles = getSideCardStyles(
        backgroundColor,
        hasPaddings,
        isPdpPage,
        isFull,
        isReversed,
        isSmallTablet,
        hasDetails,
        isExpanded,
    )

    //ssr note: initial html in the server is of layout "vertical"
    const isHorizontal = onServer ? false : layout === 'horizontal'

    const handleToggle = () => {
        setIsExpanded(!isExpanded)
    }

    const descriptionHeight = descriptionRef.current?.clientHeight
    const textHeight = mobileFullSizePdp
        ? 'fit-content'
        : isExpanded
        ? descriptionHeight
        : descriptionHeight / 4

    return (
        <Box
            {...styles.container}
            data-testid={`${isReversed ? 'reversed' : 'non-reversed'}-container`}
        >
            {isHorizontal ? (
                <Flex {...styles.horizontalContainer}>
                    <Flex
                        {...styles.horizontalOutterContent}
                        data-testid={`${isFull ? 'full' : 'app'}-container`}
                    >
                        {videoSrc ? (
                            <Box
                                data-testid="video"
                                width="100%"
                                maxWidth={isFull ? remCalc(1067) : remCalc(807)}
                                aspectRatio={damImage.ratio}
                            >
                                <video autoPlay loop muted style={{width: '100%', height: '100%'}}>
                                    <source src={videoSrc} type="video/mp4" />
                                </video>
                            </Box>
                        ) : (
                            (damImage?.url || imageUrl) && (
                                <ResponsiveImage
                                    imagelg={damImage?.url || imageUrl}
                                    alt={imageAlt}
                                    backgroundColor="transparent"
                                    imageProps={{
                                        maxWidth: isFull ? remCalc(1067) : remCalc(807),
                                        height: isPdpPage && '100%',
                                    }}
                                    {...styles.horizontalImageContainer}
                                />
                            )
                        )}

                        <Box
                            {...((damImage?.url || imageUrl || videoSrc) &&
                                styles.horizontalInnerContent)}
                        >
                            {isPdpPage ? (
                                hasDetails ? (
                                    <TextTitleDescription
                                        childrenTextStyle="body-large"
                                        alignment="left"
                                        textStyle="head-large"
                                        titleSpacing={remCalc(24)}
                                        weight="500"
                                        title={textGroupPdp?.title}
                                    >
                                        <Box
                                            data-testid="detailed-pdp"
                                            sx={{
                                                ul: {
                                                    ml: remCalc(25),
                                                },
                                                li: {
                                                    pb: remCalc(5),
                                                },
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: textGroupPdp?.bodyCopy,
                                            }}
                                        />
                                    </TextTitleDescription>
                                ) : (
                                    <TextTitleDescription
                                        titleSpacing={remCalc(24)}
                                        textStyle="head-large"
                                        childrenTextStyle="body-large"
                                        alignment="left"
                                        weight="500"
                                        title={textGroupPdp?.title}
                                    >
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: textGroupPdp?.bodyCopy,
                                            }}
                                        />
                                    </TextTitleDescription>
                                )
                            ) : (
                                <>
                                    <TextTitle
                                        textStyle="body-small"
                                        mb={remCalc(24)}
                                        color={styles.label.color}
                                        weight="700"
                                    >
                                        {textGroup?.label}
                                    </TextTitle>
                                    <TextTitleDescription
                                        textStyle="head-large"
                                        childrenTextStyle="body-large"
                                        alignment="left"
                                        weight="500"
                                        titleSpacing={remCalc(24)}
                                        color={styles.text.color}
                                        childrenTextColor={styles.text.color}
                                        mb={remCalc(24)}
                                        title={textGroup?.title}
                                    >
                                        <Box
                                            dangerouslySetInnerHTML={{__html: textGroup?.bodyCopy}}
                                        />
                                    </TextTitleDescription>
                                    {hasAppDownloads ? (
                                        <SocialContent
                                            styles={styles}
                                            appContent={appDownloadContent}
                                            alignment="left"
                                        />
                                    ) : (
                                        label &&
                                        url && (
                                            <Button variant={styles.buttonVariant} {...buttonProps}>
                                                {label}
                                            </Button>
                                        )
                                    )}
                                </>
                            )}
                        </Box>
                    </Flex>
                </Flex>
            ) : (
                <Flex {...styles.verticalContainer}>
                    <Flex {...styles.verticalOutterContent}>
                        {isPdpPage ? (
                            <>
                                {hasDetails ? (
                                    <>
                                        <Box
                                            data-testid={`${
                                                mobileFullSizePdp || isExpanded
                                                    ? 'expanded'
                                                    : 'hidden'
                                            }-text`}
                                            height={textHeight}
                                            {...(mobileFullSizePdp
                                                ? styles.verticalDetailedFullSizePdp
                                                : styles.verticalDetailedPdp)}
                                        >
                                            <TextTitleDescription
                                                childrenTextStyle="body-large"
                                                alignment="left"
                                                textStyle="head-large"
                                                titleSpacing={remCalc(24)}
                                                weight="500"
                                                title={textGroupPdp?.title}
                                            >
                                                {mobileFullSizePdp && (
                                                    <ResponsiveImage
                                                        imagelg={damImage?.url || imageUrl}
                                                        alt={imageAlt}
                                                        imageProps={{height: '100%'}}
                                                        {...styles.verticalImageContainer}
                                                    />
                                                )}

                                                <Box
                                                    ref={descriptionRef}
                                                    data-testid="detailed-pdp"
                                                    sx={{
                                                        ul: {
                                                            ml: remCalc(25),
                                                        },
                                                        li: {
                                                            pb: remCalc(5),
                                                        },
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: textGroupPdp?.bodyCopy,
                                                    }}
                                                />
                                            </TextTitleDescription>
                                        </Box>

                                        <Button
                                            variant="text-button"
                                            {...styles.toggleButton}
                                            onClick={handleToggle}
                                        >
                                            {isExpanded
                                                ? textGroupPdp?.closeCtaTitle
                                                : textGroupPdp?.expandCtaTitle}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <TextTitleDescription
                                            alignment="left"
                                            textStyle="head-large"
                                            weight="500"
                                            mb={{base: remCalc(24)}}
                                            title={textGroup.title}
                                        />
                                        {videoSrc ? (
                                            <Box
                                                data-testid="video"
                                                width="100%"
                                                height={{base: remCalc(239), md: remCalc(320)}}
                                                pb={{base: remCalc(16), md: remCalc(24)}}
                                                aspectRatio={damImage.ratio}
                                            >
                                                <video
                                                    autoPlay
                                                    loop
                                                    muted
                                                    style={{width: '100%', height: '100%'}}
                                                >
                                                    <source src={videoSrc} type="video/mp4" />
                                                </video>
                                            </Box>
                                        ) : (
                                            <ResponsiveImage
                                                imagelg={damImage?.url || imageUrl}
                                                alt={imageAlt}
                                                width="auto"
                                            />
                                        )}
                                        <TextTitleDescription
                                            alignment="left"
                                            childrenTextStyle="body-large"
                                            mt={{base: remCalc(24)}}
                                        >
                                            <Box
                                                dangerouslySetInnerHTML={{
                                                    __html: textGroup?.bodyCopy,
                                                }}
                                            />
                                        </TextTitleDescription>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {textGroup?.label && (
                                    <TextTitle
                                        color={styles.label.color}
                                        textStyle="body-small"
                                        weight="700"
                                        mb={{base: remCalc(16), md: remCalc(24)}}
                                        alignment="center"
                                    >
                                        {textGroup?.label}
                                    </TextTitle>
                                )}

                                <TextTitleDescription
                                    titleSpacing={{base: remCalc(16), md: remCalc(24)}}
                                    textStyle="head-large"
                                    alignment="center"
                                    weight="500"
                                    color={styles.text.color}
                                    title={textGroup?.title}
                                />

                                {videoSrc ? (
                                    <Box
                                        data-testid="video"
                                        // width="100%"
                                        height={{base: 'auto', md: remCalc(320)}}
                                        mb={{base: remCalc(16), md: remCalc(24)}}
                                        aspectRatio={damImage.ratio}
                                    >
                                        <video
                                            autoPlay
                                            loop
                                            muted
                                            style={{width: '100%', height: '100%'}}
                                        >
                                            <source src={videoSrc} type="video/mp4" />
                                        </video>
                                    </Box>
                                ) : (
                                    <ResponsiveImage
                                        imagelg={damImage?.url || imageUrl}
                                        alt={imageAlt}
                                        imageProps={{
                                            mb: {base: remCalc(16), md: remCalc(24)},
                                            height: '100%',
                                        }}
                                    />
                                )}

                                <TextTitleDescription
                                    mb={{base: remCalc(16), md: remCalc(24)}}
                                    childrenTextColor={styles.text.color}
                                    childrenTextStyle="body-large"
                                >
                                    <Box dangerouslySetInnerHTML={{__html: textGroup?.bodyCopy}} />
                                </TextTitleDescription>

                                {hasAppDownloads ? (
                                    <SocialContent
                                        styles={styles}
                                        appContent={appDownloadContent}
                                        alignment="center"
                                    />
                                ) : (
                                    label &&
                                    url && (
                                        <Button variant={styles.buttonVariant} {...buttonProps}>
                                            {label}
                                        </Button>
                                    )
                                )}
                            </>
                        )}
                    </Flex>
                </Flex>
            )}
        </Box>
    )
}

SideCard.propTypes = {
    textGroup: PropTypes.object,
    textGroupPdp: PropTypes.object,
    mobileFullSizePdp: PropTypes.bool,
    imageUrl: PropTypes.string,
    imageGroup: PropTypes.object,
    imagePosition: PropTypes.string,
    button: PropTypes.object || null,
    type: PropTypes.string,
    appDownloadContent: PropTypes.object,
    imageAlt: PropTypes.string,
    videoSrc: PropTypes.string,
    videos: PropTypes.object,
    hasDetails: PropTypes.bool,
    backgroundColor: PropTypes.string,
    hasPaddings: PropTypes.bool,
}
